import PropTypes from 'prop-types';
import React from "react";
import 'intl';
import 'intl/locale-data/jsonp/en';
import url from 'url';
import moment from 'moment';
import 'moment/min/locales';
import reactT from '../reactT';
import helpers from '../../helpers';

// Custom class for the result component
class FederatedResult extends React.Component {
  renderValue(field, doc) {
    const value = [].concat(doc[field] || null).filter((v) => v !== null);

    return value.join(", ");
  }

  dateFormat(date) {
    if (typeof date !== 'undefined') {
      const prettyDate = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(Date.parse(date));
      const separator = "  ·  ";
      return separator + prettyDate;
    }
  }

  dateMomentFormat(date, format) {
    if (typeof date !== 'undefined') {
      let content_lang = document.querySelector('html').getAttribute('content-lang');
      if (content_lang == 'zh-hant') {
        content_lang = 'zh-cn';
      }
      switch (format) {
        case 'full-date':
          if (document.querySelector('html').getAttribute('content-lang') == 'ar') {
            const day = moment(date).format('DD');
            const month = moment(Date.parse(date)).locale(content_lang).format("MMMM");
            const year = moment(date).format('YYYY');

            // Combine the numeric day with the translated month and day name
            return `${day} ${month} ${year}`;
          }
          else if (document.querySelector('html').getAttribute('content-lang') == 'zh-hant') {
            return moment(Date.parse(date)).locale(content_lang).format("YYYY年MMMDo");
          }
          else if (document.querySelector('html').getAttribute('content-lang') == 'de') {
            return moment(Date.parse(date)).locale(content_lang).format("D. MMMM YYYY");
          }
          else if (document.querySelector('html').getAttribute('content-lang') == 'de-ch') {
            return moment(Date.parse(date)).locale(content_lang).format("D. MMMM YYYY");
          }
          else if (document.querySelector('html').getAttribute('content-lang') == 'de-at') {
            return moment(Date.parse(date)).locale(content_lang).format("D. MMMM YYYY");
          }
          else if (document.querySelector('html').getAttribute('content-lang') == 'ja') {
            return moment(Date.parse(date)).locale(content_lang).format("YYYY年MMMMD日");
          }
          else if (document.querySelector('html').getAttribute('content-lang') == 'ko') {
            return moment(Date.parse(date)).locale(content_lang).format("YYYY년 MMMM D일");
          }
          else {
            return moment(Date.parse(date)).locale(content_lang).format("D MMMM YYYY");
          }
        case 'year-month-only':
          return moment(Date.parse(date)).locale(content_lang).format("MMMM YYYY");
        case 'year-only':
          return moment(Date.parse(date)).locale(content_lang).format("YYYY");
        default:
          return moment(Date.parse(date)).locale(content_lang).format("D/MMMM Y");
      }
    }
  }

  handleClick(currentValue, currentField) {
    let { query } = this.props;
    let searchTerm = "";
    // Keep only the value of the main search field.
    for (const field of query.searchFields) {
      if (
        field.field !== query.mainQueryField &&
        field.field !== "ss_search_api_language" &&
        field.id !== "textSearchField" &&
        field.id !== "type"
      ) {
        // Remove the field value.
        delete field.value;
        // Collapse the sidebar filter toggle.
        field.collapse = true;
        // Collapse the terms sidebar filter toggle.
        if (Object.hasOwnProperty.call(field, "expandedHierarchies")) {
          field.expandedHierarchies = [];
        }
      } else if (
        field.field === query.mainQueryField ||
        field.id === "textSearchField"
      ) {
        // Extract the value of the main search term to use when setting new URL for this state.
        searchTerm = field.value;
      }
    }
    // Set new parsed params based on only search term value.
    const parsed = {
      search: searchTerm,
    };

    // Update state to remove the filter field values.
    this.setState({ query });

    // Add current field to Qs.
    let newParsed = helpers.qs.addQsParam({
      field: currentField,
      value: currentValue,
      parsed,
    });

    this.props.onChange(currentField, [currentValue]);

    helpers.qs.addNewUrlToBrowserHistory(newParsed);
  }

  /**
   * Returns the appropriate link for a given search result.
   * 1. Use the canonical url if present.
   * 2. Next, attempt to find a url from the current host.
   * 3. Then, use the first url returned in the array of urls.
   * 4. Fall back to the legacy url field.
   * 5. Send an empty string if none of these options work.
   *
   * @param doc
   *   Search result object
   * @returns string
   */
  getCanonicalLink(doc) {
    const { hostname } = this.props;

    // 1. Use the canonical url as a first option
    if (Object.hasOwnProperty.call(doc, 'ss_canonical_url') && doc.ss_canonical_url) {
      return doc.ss_canonical_url;
    }

    // Use a url from the current host, if present.  Otherwise use the first Url.
    if (Object.hasOwnProperty.call(doc, 'sm_urls') && Array.isArray(doc.sm_urls)) {
      // Attempt to find a url from the current host.
      const currentHostUrl = doc.sm_urls.find((item) => {
        const myUrl = url.parse(item);
        return myUrl.hostname === hostname;
      });

      // 2. Use the url from the current host.
      if (currentHostUrl) {
        return currentHostUrl;
      }

      // 3. Use the first url.
      return doc.sm_urls[0];
    }

    // 4. Fall back to the single url (which will be relative)
    if (Object.hasOwnProperty.call(doc, 'ss_url') && doc.ss_url) {
      return doc.ss_url;
    }
    else if (Object.hasOwnProperty.call(doc, 'ss_url_1') && doc.ss_url_1) {
      return doc.ss_url_1;
    }
    else if (Object.hasOwnProperty.call(doc, 'ss_url_2') && doc.ss_url_2) {
      return doc.ss_url_2;
    }
    else if (Object.hasOwnProperty.call(doc, 'ss_url_3') && doc.ss_url_3) {
      return doc.ss_url_3;
    }

    // 5. If no valid urls are passed, return nothing. This will result in an
    // unlinked title, but at least it won't crash.
    return '';
  }

  intersperse(arr, sep) {
    if (arr.length === 0) {
      return [];
    }

    return arr.slice(1).reduce(function (xs, x, i) {
      return xs.concat([sep, x]);
    }, [arr[0]]);
  }

  renderSitenameLinks(sitenames, urls, originalSitename) {
    if (sitenames != null && urls != null) {

      var sites = [];
      for (var i = 0; i < sitenames.length; i++) {
        sites.push(<a className="search-results__site-name" href={urls[i]} key={i}>{sitenames[i]}</a>);
        if (i !== (sitenames.length - 1)) {

        }
      }
      return this.intersperse(sites, " | ");
    }

    if (originalSitename != null) {
      return originalSitename;
    }

    return null;
  }

  linkSanitizer(link) {
    if (link) {
      return link.toString().replace('entity:', '');
    }
  }

  render() {
    const { doc, highlight } = this.props;

    let image = '';
    let contentWrapperClasses = 'content-wrapper col-lg-12 col';
    /*global drupalSettings:true*/
    let upcomingEvent = '';
    let pastEvent = '';
    if (typeof drupalSettings !== 'undefined' && drupalSettings.type === 'event') {
      // Check if past or upcoming event.
      if (moment.now() < Date.parse(doc.ds_field_date_end_value)) {
        upcomingEvent = <div className='upcoming'>{reactT('Upcoming')}</div>;
      }
      else {
        pastEvent = ' past';
      }
    }
    if (doc.ss_url !== 'undefined' && doc.ss_url != null) {
      if (typeof drupalSettings !== 'undefined') {
        if (drupalSettings.type === 'main') {
          image = <div className="image-wrapper col-xl-2 col-lg-3 col-4"><img src={doc.ss_url} alt={doc.ss_title} /></div>;
          contentWrapperClasses = 'content-wrapper col-xl-10 col-lg-9 col-8';
        }
        else {
          image = <div className={"image-wrapper col-md-12 col-4" + pastEvent}>{upcomingEvent}<img src={doc.ss_url_d04_square} alt={doc.ss_title} /></div>;
          contentWrapperClasses = 'content-wrapper col-md-12 col';
        }
      }
    }
    else if (doc.ss_url_1 !== 'undefined' && doc.ss_url_1 != null) {
      if (typeof drupalSettings !== 'undefined') {
        if (drupalSettings.type === 'main') {
          image = <div className="image-wrapper col-xl-2 col-lg-3 col-4"><img src={doc.ss_url_1} alt={doc.ss_title} /></div>;
          contentWrapperClasses = 'content-wrapper col-xl-10 col-lg-9 col-8';
        }
        else {
          image = <div className={"image-wrapper col-md-12 col-4" + pastEvent}>{upcomingEvent}<img src={doc.ss_url_4_d04_square} alt={doc.ss_title} /></div>;
          contentWrapperClasses = 'content-wrapper col-md-12 col';
        }
      }
    }
    else if (doc.ss_url_2 !== 'undefined' && doc.ss_url_2 != null) {
      if (typeof drupalSettings !== 'undefined') {
        if (drupalSettings.type === 'main') {
          image = <div className="image-wrapper col-xl-2 col-lg-3 col-4"><img src={doc.ss_url_2} alt={doc.ss_title} /></div>;
          contentWrapperClasses = 'content-wrapper col-xl-10 col-lg-9 col-8';
        }
        else {
          image = <div className={"image-wrapper col-md-12 col-4" + pastEvent}>{upcomingEvent}<img src={doc.ss_url_5_d04_square} alt={doc.ss_title} /></div>;
          contentWrapperClasses = 'content-wrapper col-md-12 col';
        }
      }
    }
    else if (doc.ss_url_3 !== 'undefined' && doc.ss_url_3 != null) {
      if (typeof drupalSettings !== 'undefined') {
        if (drupalSettings.type === 'main') {
          image = <div className="image-wrapper col-xl-2 col-lg-3 col-4"><img src={doc.ss_url_3} alt={doc.ss_name} /></div>;
          contentWrapperClasses = 'content-wrapper col-xl-10 col-lg-9 col-8';
        }
        else {
          // Handle PDF files in the publication.
          if (drupalSettings.type === 'publication' && (doc.ss_url_4 !== 'undefined' && doc.ss_url_4 != null)) {
            image = <div className={"image-wrapper col-md-12 col-4" + pastEvent}>{upcomingEvent}<img src={doc.ss_url_4} alt={doc.ss_title} /></div>;
          } else {
            image = <div className={"image-wrapper col-md-12 col-4" + pastEvent}>{upcomingEvent}<img src={doc.ss_url_3} alt={doc.ss_title} /></div>;
          }

          contentWrapperClasses = 'content-wrapper col-md-12 col';
        }
      }
    }
    else if (typeof drupalSettings !== 'undefined' && drupalSettings.type !== 'main') {
      image = <div className={"image-wrapper col-md-12 col-4" + pastEvent}>{upcomingEvent}<img src="/themes/custom/project/images/cropped-views-default-image.jpeg" alt={doc.ss_title} /></div>;
      contentWrapperClasses = 'content-wrapper col-md-12 col';
    }

    let topic = '';
    if (doc.sm_topics !== 'undefined' && doc.sm_topics != null) {
      const listItems = doc.sm_topics.map((name, index) =>
        <div onClick={() => this.handleClick(name, 'sm_topics')} key={index} className="field__item">
          {name}
        </div>);
      topic = <div className="field--name-field-topic field__items">{listItems}</div>;
    }
    let category = '';
    if (doc.ss_field_categories_list !== 'undefined' && doc.ss_field_categories_list != null && typeof drupalSettings !== 'undefined') {
      let categoryValue = drupalSettings.categories_list_labels[doc.ss_field_categories_list].label;
      category = <div onClick={() => this.handleClick(doc.ss_field_categories_list, 'ss_field_categories_list')} className="field--name-field-categories-list"><span className="category-label">{reactT('Category')}:</span><span className="field__item"><span>{categoryValue}</span></span></div>
    }

    let name = doc.ss_title;
    if (doc.ss_name !== 'undefined' && doc.ss_name != null) {
      name = doc.ss_name;
    }

    let eventType = doc.ss_field_event_type;
    if (doc.ss_field_event_type !== 'undefined' && doc.ss_field_event_type != null) {
      switch (doc.ss_field_event_type) {
        case "0":
          eventType = reactT('Private event')
          break;
        case "1":
          eventType = reactT('Public event')
          break;
        case "2":
          eventType = reactT('Invite-only event')
          break;
        default:
          eventType = reactT('Public event')
          break;
      }
    }

    let date = '';
    if (doc.ds_created !== 'undefined' && doc.ds_created != null) {
      date = doc.ds_created;
    }

    let siteurl = this.getCanonicalLink(doc).replace(/^https?:\/\//, '');

    if (typeof drupalSettings !== 'undefined') {
      /*global drupalSettings:true*/
      if (drupalSettings.type === 'publication') {
        let dateFormat = doc.ss_field_date_format || 'full-date';

        return (
          <div className="col-sm-12 col-md-6 col-lg-4 views-row">
            <div className="node--view-mode-vertical-media-teaser-medium">
              <div className="main-content row">
                {image}
                <div className={contentWrapperClasses}>
                  <div className="field--name-node-title"><h4 className="search-results__heading"><a href={this.getCanonicalLink(doc)} dangerouslySetInnerHTML={{ __html: name }} /></h4></div>
                  <div className="search-excerpt" dangerouslySetInnerHTML={{ __html: highlight[Object.keys(highlight)[0]] }} />
                  <div className="field field--name-node-post-date field__item">{this.dateMomentFormat(doc.ds_created, dateFormat)}</div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      else if (drupalSettings.type === 'event') {
        return (
          <div className="col-sm-12 col-md-6 col-lg-4 views-row">
            <div className="node--view-mode-vertical-media-teaser-medium">
              <div className="main-content row">
                {image}
                <div className={contentWrapperClasses}>
                  <div className="field--name-node-title"><h4 className="search-results__heading"><a href={this.getCanonicalLink(doc)} dangerouslySetInnerHTML={{ __html: name }} /></h4></div>
                  <div className="field--name-field-date">
                    <div class="field__label">{reactT('Date')}</div>
                    <div class="field__item">{this.dateMomentFormat(doc.ds_field_date, 'full-date')}</div>
                  </div>
                  <div className="search-excerpt" dangerouslySetInnerHTML={{ __html: highlight[Object.keys(highlight)[0]] }} />
                  <div class="field--name-field-event-type">
                    <div class="field__label">{reactT('Audience')}</div>
                    <div class="field__item">{eventType}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      else if (drupalSettings.type === 'news') {
        if (doc.ss_news_category != 'Uncategorized') {
          return (
            <div className="col-sm-12 col-md-6 col-lg-4 views-row">
              <div className="node--view-mode-vertical-media-teaser-medium">
                <div className="main-content row">
                  {image}
                  <div className={contentWrapperClasses}>
                    <div className="field--name-node-title"><h4 className="search-results__heading"><a href={this.getCanonicalLink(doc)} dangerouslySetInnerHTML={{ __html: name }} /></h4></div>
                    <div className="search-excerpt" dangerouslySetInnerHTML={{ __html: highlight[Object.keys(highlight)[0]] }} />
                    <div className="field field--name-node-post-date field__item">{this.dateMomentFormat(doc.ds_created, 'full-date')}</div>
                    <div onClick={() => this.handleClick(doc.ss_news_category, 'ss_news_category')} className="field field--name-field-categories field__item">{doc.ss_news_category}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        else {
          return (
            <div className="col-sm-12 col-md-6 col-lg-4 views-row">
              <div className="node--view-mode-vertical-media-teaser-medium">
                <div className="main-content row">
                  {image}
                  <div className={contentWrapperClasses}>
                    <div className="field--name-node-title"><h4 className="search-results__heading"><a href={this.getCanonicalLink(doc)} dangerouslySetInnerHTML={{ __html: name }} /></h4></div>
                    <div className="search-excerpt" dangerouslySetInnerHTML={{ __html: highlight[Object.keys(highlight)[0]] }} />
                    <div className="field field--name-node-post-date field__item">{this.dateMomentFormat(doc.ds_created, 'full-date')}</div>
                    <div onClick={() => this.handleClick(doc.ss_news_category, 'ss_news_category')} className="field field--name-field-categories field__item"></div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
      }

      else if (drupalSettings.type === 'supporters') {
        return (
          <div className="col-sm-12 col-md-6 col-lg-4 views-row">
            <div className="node--view-mode-vertical-media-teaser-medium">
              <div className="main-content row">
                {image}
                <div className={contentWrapperClasses}>
                  <div className="field--name-node-title"><h4 className="search-results__heading"><a href={this.getCanonicalLink(doc)} dangerouslySetInnerHTML={{ __html: name }} /></h4></div>
                  <div className="search-excerpt" dangerouslySetInnerHTML={{ __html: highlight[Object.keys(highlight)[0]] }} />
                </div>
              </div>
            </div>
          </div>
        );
      }
    }

    return (
      <div className="views-row node--view-mode-search-result">
        <div className="search-results__container--right">
          <div className="row bs-region bs-region--main">
            {image}
            <div className={contentWrapperClasses}>
              <div className="url-wrapper"><a className="site-link" href={this.getCanonicalLink(doc)}>{siteurl}</a></div>
              {(() => {
                if ((doc.ss_field_citation && doc.ss_field_citation != 'undefined' && doc.ss_field_citation != null) && (doc.ss_filemime && doc.ss_filemime == 'application/pdf')) {
                  return (
                    <div className="field--name-node-title"><h4 className="search-results__heading"><a href={this.getCanonicalLink(doc)} dangerouslySetInnerHTML={{ __html: doc.ss_field_citation + ' ' + name }} /></h4></div>
                  )
                }
                else {
                  return (
                    <div className="field--name-node-title"><h4 className="search-results__heading"><a href={this.getCanonicalLink(doc)} dangerouslySetInnerHTML={{ __html: name }} /></h4></div>
                  )
                }
              })()
              }
              {(() => {
                if ((date && date != 'undefined' && date != null) && (doc.ss_filemime && doc.ss_filemime == 'application/pdf')) {
                  let content_lang = document.querySelector('html').getAttribute('content-lang');
                  if (content_lang == 'zh-hant') {
                    return (
                      <div className="field--date"><span className="date-label">{moment(Date.parse(date)).locale(content_lang).format("YYYY年MMM")}</span></div>
                    )
                  }
                  return (
                    <div className="field--date"><span className="date-label">{moment(Date.parse(date)).locale(content_lang).format("MMMM") + ' ' + moment(Date.parse(date)).format("YYYY")}</span></div>
                  )
                }
              })()
              }
              {(() => {
                if ((doc.ss_processed && doc.ss_processed != 'undefined' && doc.ss_processed != null) && (doc.ss_filemime && doc.ss_filemime == 'application/pdf')) {
                  return (
                    <div className="search-excerpt" dangerouslySetInnerHTML={{ __html: doc.ss_processed }} />
                  )
                }
                else {
                  return (
                    <div className="search-excerpt" dangerouslySetInnerHTML={{ __html: highlight[Object.keys(highlight)[0]] }} />
                  )
                }
              })()
              }
              <div className="result-filters">
                {category}
                {topic}
              </div>
              <div>
                <div className='attachment'>
                  {(() => {
                    if (this.linkSanitizer(doc.sm_field_link_attachment) || doc.sm_document_attachment_url) {
                      return (
                        <div>{reactT('Attachments')}:</div>
                      )
                    }
                  })()
                  }
                  {doc.sm_field_link_attachment?.map((key, value) => (
                    <a href={this.linkSanitizer(key)}>
                      {doc.sm_file_link_text[value]}
                    </a>
                  ))}
                  {(() => {
                    let content_lang = document.querySelector('html').getAttribute('content-lang');
                    if (content_lang === 'zh-hant') content_lang = 'zh-cn';
                    let qwq = `tm_X3b_${content_lang}_document_attachment_url_full_text`;

                    return doc[qwq]?.map((key, value) => {
                      const descKey = `tm_X3b_${content_lang}_description`;
                      const description = doc[descKey]?.[value];
                      const name = doc.sm_document_attachment_name?.[value] || '';
                      const label = (description && description.trim() !== '') ? description : name;
                      return (
                        <a href={this.linkSanitizer(key)} key={value}>
                          {label}
                        </a>
                      );
                    });
                  })()}
                </div>
              </div>
              <div className="authoring-information">
                {(() => {
                  if (doc.ss_type == 'event' || doc.ss_type == 'news' || doc.ss_type == 'publication') {
                    let content_lang = document.querySelector('html').getAttribute('content-lang');
                    if (content_lang == 'zh-hant') {
                      content_lang = 'zh-cn';
                    }

                    if (document.querySelector('html').getAttribute('content-lang') == 'ar') {
                      return (
                        <div className="field field--name-node-post-date field__item">
                          <span className="date-label">{reactT('Date')}:</span>
                          <span className="date">{moment(date).format('DD')} {moment(Date.parse(date)).locale(content_lang).format("MM")} {moment(date).format('YYYY')}</span>
                        </div>
                      )
                    }
                    else if (document.querySelector('html').getAttribute('content-lang') == 'zh-hant') {
                      return (
                        <div className="field field--name-node-post-date field__item">
                          <span className="date-label">{reactT('Date')}:</span>
                          <span className="date">{moment(Date.parse(date)).locale(content_lang).format("YYYY年MMMDo")}</span>
                        </div>
                      )
                    }
                    else if (document.querySelector('html').getAttribute('content-lang') == 'de') {
                      return (
                        <div className="field field--name-node-post-date field__item">
                          <span className="date-label">{reactT('Date')}:</span>
                          <span className="date">{moment(Date.parse(date)).locale(content_lang).format("D. MMMM YYYY")}</span>
                        </div>
                      )
                    }
                    else if (document.querySelector('html').getAttribute('content-lang') == 'de-ch') {
                      return (
                        <div className="field field--name-node-post-date field__item">
                          <span className="date-label">{reactT('Date')}:</span>
                          <span className="date">{moment(Date.parse(date)).locale(content_lang).format("D. MMMM YYYY")}</span>
                        </div>
                      )
                    }
                    else if (document.querySelector('html').getAttribute('content-lang') == 'de-at') {
                      return (
                        <div className="field field--name-node-post-date field__item">
                          <span className="date-label">{reactT('Date')}:</span>
                          <span className="date">{moment(Date.parse(date)).locale(content_lang).format("D. MMMM YYYY")}</span>
                        </div>
                      )
                    }
                    else if (document.querySelector('html').getAttribute('content-lang') == 'ja') {
                      return (
                        <div className="field field--name-node-post-date field__item">
                          <span className="date-label">{reactT('Date')}:</span>
                          <span className="date">{moment(Date.parse(date)).locale(content_lang).format("YYYY年MMMMD日")}</span>
                        </div>
                      )
                    }
                    else if (document.querySelector('html').getAttribute('content-lang') == 'ko') {
                      return (
                        <div className="field field--name-node-post-date field__item">
                          <span className="date-label">{reactT('Date')}:</span>
                          <span className="date">{moment(Date.parse(date)).locale(content_lang).format("YYYY년 MMMM D일")}</span>
                        </div>
                      )
                    }
                    else {
                      return (
                        <div className="field field--name-node-post-date field__item">
                          <span className="date-label">{reactT('Date')}:</span>
                          <span className="date">{moment(Date.parse(date)).locale(content_lang).format("D MMMM YYYY")}</span>
                        </div>
                      )
                    }
                  }
                })()
                }
                <div className="source-wrapper">
                  <span>{reactT('Site')}:</span><a className="page-source" href={doc.site}>{doc.sm_site_name}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

FederatedResult.propTypes = {
  doc: PropTypes.object,
  fields: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  hostname: PropTypes.string,
};

export default FederatedResult;
